import React from 'react';
import { FaCalculator, FaMapMarkerAlt, FaUser, FaDollarSign } from 'react-icons/fa';
import { LegacyIncomeData, RegionalFactors, TradeSpecificFactors } from '../types';
import { US_STATES, LEGACY_TRADE_TYPES, OVERTIME_MULTIPLIERS } from '../utils/constants';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';

interface CalculatorFormProps {
  income: Partial<LegacyIncomeData>;
  regional: Partial<RegionalFactors>;
  tradeFactors: Partial<TradeSpecificFactors>;
  onIncomeChange: (income: Partial<LegacyIncomeData>) => void;
  onRegionalChange: (regional: Partial<RegionalFactors>) => void;
  onTradeFactorsChange: (factors: Partial<TradeSpecificFactors>) => void;
  onCalculate: () => void;
  loading: boolean;
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({
  income,
  regional,
  tradeFactors,
  onIncomeChange,
  onRegionalChange,
  onTradeFactorsChange,
  onCalculate,
  loading
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-3">
          <FaCalculator className="text-primary text-xl" />
          <span>Calculate Your Housing Budget</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
      
      <div className="space-y-8">
        {/* Income Section */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <FaDollarSign className="text-primary" />
            <h3 className="text-lg font-medium">Income Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="baseHourlyRate">Base Hourly Rate ($)</Label>
              <Input
                id="baseHourlyRate"
                type="number" 
                placeholder="25.00"
                value={income.baseHourlyRate || ''}
                onChange={(e) => onIncomeChange({ 
                  ...income, 
                  baseHourlyRate: parseFloat(e.target.value) || 0 
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="regularHours">Regular Hours per Week</Label>
              <Input
                id="regularHours"
                type="number" 
                placeholder="40"
                value={income.regularHours || ''}
                onChange={(e) => onIncomeChange({ 
                  ...income, 
                  regularHours: parseFloat(e.target.value) || 0 
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="overtimeHours">Overtime Hours per Week</Label>
              <Input
                id="overtimeHours"
                type="number" 
                placeholder="10"
                value={income.overtimeHours || ''}
                onChange={(e) => onIncomeChange({ 
                  ...income, 
                  overtimeHours: parseFloat(e.target.value) || 0 
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Overtime Rate Multiplier</Label>
              <Select 
                value={String(income.overtimeMultiplier || 1.5)}
                onValueChange={(value) => onIncomeChange({ 
                  ...income, 
                  overtimeMultiplier: parseFloat(value) 
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OVERTIME_MULTIPLIERS.map(mult => (
                    <SelectItem key={mult.value} value={String(mult.value)}>
                      {mult.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="weeksPerYear">Weeks Worked per Year</Label>
              <Input
                id="weeksPerYear"
                type="number" 
                placeholder="50"
                value={income.weeksPerYear || ''}
                onChange={(e) => onIncomeChange({ 
                  ...income, 
                  weeksPerYear: parseFloat(e.target.value) || 50 
                })}
              />
            </div>
          </div>
        </div>

        {/* Regional Factors */}
        <div>
          <Separator className="my-6" />
          <div className="flex items-center space-x-2 mb-4">
            <FaMapMarkerAlt className="text-primary" />
            <h3 className="text-lg font-medium">Location & Regional Factors</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>State</Label>
              <Select 
                value={regional.state || ''}
                onValueChange={(value) => onRegionalChange({ 
                  ...regional, 
                  state: value 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(state => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Area Type</Label>
              <Select 
                value={regional.cityType || 'suburban'}
                onValueChange={(value) => onRegionalChange({ 
                  ...regional, 
                  cityType: value as 'urban' | 'suburban' | 'rural'
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urban">Urban (City center, high density)</SelectItem>
                  <SelectItem value="suburban">Suburban (Residential neighborhoods)</SelectItem>
                  <SelectItem value="rural">Rural (Country, small towns)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Trade-Specific Factors */}
        <div>
          <Separator className="my-6" />
          <div className="flex items-center space-x-2 mb-4">
            <FaUser className="text-primary" />
            <h3 className="text-lg font-medium">Trade-Specific Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Trade Type</Label>
              <Select 
                value={tradeFactors.tradeType || ''}
                onValueChange={(value) => onTradeFactorsChange({ 
                  ...tradeFactors, 
                  tradeType: value 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your trade" />
                </SelectTrigger>
                <SelectContent>
                  {LEGACY_TRADE_TYPES.map(trade => (
                    <SelectItem key={trade} value={trade}>
                      {trade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Union Member?</Label>
              <Select 
                value={tradeFactors.unionMember ? 'yes' : 'no'}
                onValueChange={(value) => onTradeFactorsChange({ 
                  ...tradeFactors, 
                  unionMember: value === 'yes' 
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="toolAllowance">Monthly Tool/Equipment Costs ($)</Label>
              <Input
                id="toolAllowance"
                type="number" 
                placeholder="200"
                value={tradeFactors.toolAllowance || ''}
                onChange={(e) => onTradeFactorsChange({ 
                  ...tradeFactors, 
                  toolAllowance: parseFloat(e.target.value) || 0 
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="transportationCosts">Monthly Transportation Costs ($)</Label>
              <Input
                id="transportationCosts"
                type="number" 
                placeholder="400"
                value={tradeFactors.transportationCosts || ''}
                onChange={(e) => onTradeFactorsChange({ 
                  ...tradeFactors, 
                  transportationCosts: parseFloat(e.target.value) || 0 
                })}
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="seasonalWork"
                  checked={tradeFactors.seasonalWork || false}
                  onCheckedChange={(checked) => onTradeFactorsChange({ 
                    ...tradeFactors, 
                    seasonalWork: !!checked
                  })}
                />
                <Label htmlFor="seasonalWork" className="text-sm">
                  My work is seasonal or has significant periods with reduced hours
                </Label>
              </div>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <Separator className="my-6" />
        <div className="flex justify-center md:justify-start">
          <Button 
            onClick={onCalculate}
            disabled={loading}
            className="w-full md:w-auto"
          >
            {loading ? 'Calculating...' : 'Calculate Housing Budget'}
          </Button>
        </div>
      </div>
    </CardContent>
    </Card>
  );
};

export default CalculatorForm;